import { Button, Tooltip, Badge } from "flowbite-react";
import { HiEye, HiPencil, HiTrash, HiBan, HiRefresh } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ClientGroup, DataGridPagination } from "@/sharedTypes";
import DataTable from "../Table";
import ClientGroupFormDialog from "./ClientGroupFormDialog";
import {
  useUpdateClientGroupMutation,
  useDisableClientGroupMutation,
  useRestoreClientGroupMutation,
  useHardDeleteClientGroupMutation,
} from "@/api/clientGroupApi/clientGroup";
import { toast } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

interface ClientGroupTableProps {
  data: ClientGroup[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  showInactive: boolean;
}

const ClientGroupTable = ({
  data,
  pagination,
  onPageChange,
  showInactive,
}: ClientGroupTableProps) => {
  const { t } = useTranslation(["clientGroup"]);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<
    ClientGroup | undefined
  >(undefined);

  const [updateClientGroup] = useUpdateClientGroupMutation();
  const [disableClientGroup] = useDisableClientGroupMutation();
  const [restoreClientGroup] = useRestoreClientGroupMutation();
  const [hardDeleteClientGroup] = useHardDeleteClientGroupMutation();

  const handleEditClick = (group: ClientGroup) => {
    setSelectedGroup(group);
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (updatedData: ClientGroup) => {
    if (!selectedGroup) return;
    try {
      const payload: Partial<ClientGroup> = {
        group_name: updatedData.group_name,
        group_description: updatedData.group_description,
      };

      await updateClientGroup({
        clientGroupId: selectedGroup.client_group_id.toString(),
        updatedDetails: payload,
      }).unwrap();
      toast.success(t("clientGroup:updateSuccess"));
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error(t("clientGroup:updateFail"));
    }
  };

  const handleDisableClick = async (id: string) => {
    if (confirm(t("clientGroup:disableConfirm"))) {
      try {
        await disableClientGroup(id).unwrap();
        toast.success(t("clientGroup:disableSuccess"));
      } catch (error) {
        toast.error(t("clientGroup:disableFail"));
      }
    }
  };

  const handleRestoreClick = async (id: number) => {
    try {
      await restoreClientGroup(id).unwrap();
      toast.success(t("clientGroup:restoreSuccess"));
    } catch (error) {
      toast.error(t("clientGroup:restoreFail"));
    }
  };

  const handleHardDeleteClick = async (id: number) => {
    if (confirm(t("clientGroup:deleteConfirm"))) {
      try {
        await hardDeleteClientGroup(id).unwrap();
        toast.success(t("clientGroup:deleteSuccess"));
      } catch (error) {
        toast.error(t("clientGroup:deleteFail"));
      }
    }
  };

  const columns = [
    { key: "group_name", label: t("clientGroup:groupName") },
    {
      key: "group_description",
      label: t("clientGroup:description"),
      render: (row: ClientGroup) => (
        <span>
          {row.group_description && row.group_description.length > 50
            ? `${row.group_description.slice(0, 50)}...`
            : row.group_description || ""}
        </span>
      ),
    },
    {
      key: "is_active",
      label: t("clientGroup:status"),
      render: (row: ClientGroup) => (
        <Badge color={row.is_active ? "success" : "failure"}>
          {row.is_active
            ? t("clientGroup:active")
            : t("clientGroup:inactive")}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      label: t("clientGroup:createdAt"),
      render: (row: ClientGroup) => (
        <span>
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString()
            : ""}
        </span>
      ),
    },
    {
      key: "actions",
      label: t("clientGroup:actions"),
      render: (row: ClientGroup) => (
        <div className="flex gap-2">
          {!showInactive ? (
            <>
              <Tooltip content={t("clientGroup:view")}>
                <Button
                  color="light"
                  size="xs"
                  onClick={() =>
                    row.client_group_id &&
                    navigate(`/client-groups/${row.client_group_id}`)
                  }
                >
                  <HiEye className="h-4 w-4 text-blue-500" />
                </Button>
              </Tooltip>
              {isCompanyAdminUser(user) && (
                <>
                  <Tooltip content={t("clientGroup:edit")}>
                    <Button
                      color="light"
                      size="xs"
                      onClick={() =>
                        row.client_group_id && handleEditClick(row)
                      }
                    >
                      <HiPencil className="h-4 w-4 text-purple-500" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={t("clientGroup:disable")}>
                    <Button
                      color="light"
                      size="xs"
                      onClick={() =>
                        row.client_group_id &&
                        handleDisableClick(
                          row.client_group_id.toString()
                        )
                      }
                    >
                      <HiBan className="h-4 w-4 text-orange-500" />
                    </Button>
                  </Tooltip>
                </>
              )}
            </>
          ) : (
            <>
              {isCompanyAdminUser(user) && (
                <>
                  <Tooltip content={t("clientGroup:restore")}>
                    <Button
                      color="light"
                      size="xs"
                      onClick={() =>
                        row.client_group_id &&
                        handleRestoreClick(row.client_group_id)
                      }
                    >
                      <HiRefresh className="h-4 w-4 text-green-500" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={t("clientGroup:permanentDelete")}>
                    <Button
                      color="light"
                      size="xs"
                      onClick={() =>
                        row.client_group_id &&
                        handleHardDeleteClick(row.client_group_id)
                      }
                    >
                      <HiTrash className="h-4 w-4 text-red-500" />
                    </Button>
                  </Tooltip>
                </>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />
      <ClientGroupFormDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSubmit={handleEditSubmit}
        mode="edit"
        initialData={selectedGroup}
      />
    </>
  );
};

export default ClientGroupTable;
